import React, { useState } from 'react';
import { TripsLayout } from '../../components/layout/TripsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { MapPin, ChevronLeft, ChevronRight } from 'lucide-react';

interface Travel {
  id: string;
  city: string;
  reason: string;
  date_from: string;
  date_to: string;
  flight_cost: number;
  hotel_cost: number;
  status: string;
  approvals_needed: number;
  created_at: string;
}

interface IndexProps {
  travels: Travel[];
  user: {
    id: string;
    name: string;
    email: string;
    is_admin: boolean;
  };
  routes: {
    root: string;
    travels_index: string;
    travels_new: string;
    travels_create: string;
    sessions_new: string;
    sessions_create: string;
    sessions_destroy: string;
  };
}

// Helper function to get week start (Monday)
const getWeekStart = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday is first day
  return new Date(d.setDate(diff));
};

// Helper function to get week end (Sunday)
const getWeekEnd = (date: Date) => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
};

// Helper function to add weeks
const addWeeks = (date: Date, weeks: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + (weeks * 7));
  return result;
};

// Helper function to format week number
const getWeekNumber = (date: Date) => {
  const start = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + start.getDay() + 1) / 7);
};

// Helper function to check if travel overlaps with week
const isTravelInWeek = (travel: Travel, weekStart: Date, weekEnd: Date) => {
  const travelStart = new Date(travel.date_from);
  const travelEnd = new Date(travel.date_to);

  return (
    (travelStart >= weekStart && travelStart <= weekEnd) ||
    (travelEnd >= weekStart && travelEnd <= weekEnd) ||
    (travelStart <= weekStart && travelEnd >= weekEnd)
  );
};

export default function Index({ travels, user, routes }: IndexProps) {
  const [startDate, setStartDate] = useState(getWeekStart(new Date()));

  // Generate 12 weeks from the start date
  const weeks = Array.from({ length: 12 }, (_, i) => {
    const weekStart = getWeekStart(addWeeks(startDate, i));
    return {
      start: weekStart,
      end: getWeekEnd(weekStart),
      weekNumber: getWeekNumber(weekStart)
    };
  });

  // Function to navigate weeks
  const navigateWeeks = (direction: 'prev' | 'next') => {
    setStartDate(prev =>
      direction === 'prev'
        ? addWeeks(prev, -12)
        : addWeeks(prev, 12)
    );
  };

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get status color for travel entries
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-600 hover:bg-green-700';
      case 'rejected':
        return 'bg-destructive hover:bg-destructive/90';
      default:
        return 'bg-yellow-600 hover:bg-yellow-700';
    }
  };


  return (
    <TripsLayout>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-2xl">Travel Timeline</CardTitle>
          <CardDescription>Overview of your travel requests over the next 12 weeks</CardDescription>
        </CardHeader>
      </Card>

      {/* Temporarily hidden calendar view
      <div className="flex justify-between mb-4">
        <Button
          variant="outline"
          onClick={() => navigateWeeks('prev')}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous 12 Weeks
        </Button>
        <Button
          variant="outline"
          onClick={() => navigateWeeks('next')}
          className="flex items-center gap-2"
        >
          Next 12 Weeks
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {travels.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <div className="text-gray-500 mb-8">
              <div className="text-4xl mb-4">✈️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No travel requests yet</h3>
              <p className="text-sm text-gray-600">
                Start by creating your first travel request
              </p>
            </div>
            <Button onClick={() => window.location.href = routes.travels_new}>
              Create Travel Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[1200px]">
            <div className="mb-4" style={{
              display: 'grid',
              gridTemplateColumns: '200px repeat(12, 1fr)',
              gap: 0
            }}>
              <div className="font-medium p-3">My Travel</div>
              {weeks.map((week, idx) => (
                <div key={idx} className="text-center font-medium p-3">
                  <div className="text-sm whitespace-nowrap">Week {week.weekNumber}</div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(week.start)} - {formatDate(week.end)}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-lg hover:bg-gray-50 transition-colors" style={{
              display: 'grid',
              gridTemplateColumns: '200px repeat(12, 1fr)',
              gap: 0
            }}>
              <div className="p-3 font-medium bg-gray-50">{user.name}</div>

              {weeks.map((week, weekIdx) => {
                const travelEntry = travels.find(travel =>
                  travel.status === 'Approved' && isTravelInWeek(travel, week.start, week.end)
                );

                return (
                  <div key={weekIdx} className="p-2 min-h-[80px] flex items-center justify-center hover:bg-gray-100 transition-colors">
                    {travelEntry ? (
                      <div
                        className={`${getStatusColor(travelEntry.status)} rounded-md p-2 text-white text-xs flex items-center justify-center cursor-pointer min-w-0 w-full max-w-[120px] transition-colors`}
                        title={`${travelEntry.city}\n${formatDate(new Date(travelEntry.date_from))} - ${formatDate(new Date(travelEntry.date_to))}\n${travelEntry.reason}`}
                      >
                        <MapPin className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{travelEntry.city}</span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      */}

      {/* All Travel Requests section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">All Travel Requests</h3>

        {travels.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-gray-500 mb-8">
                <div className="text-4xl mb-4">✈️</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No travel requests yet</h3>
                <p className="text-sm text-gray-600">
                  Start by creating your first travel request
                </p>
              </div>
              <Button onClick={() => window.location.href = routes.travels_new}>
                Create Travel Request
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {travels.map((travel) => (
              <Card key={travel.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{travel.city}</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      travel.status === 'Approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                      travel.status === 'Rejected' ? 'bg-destructive/10 text-destructive dark:bg-destructive/20' :
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                    }`}>
                      {travel.status}
                    </span>
                  </div>

                  <div className="text-sm text-muted-foreground mb-1">
                    {formatDate(new Date(travel.date_from))} - {formatDate(new Date(travel.date_to))}
                  </div>

                  <div className="text-sm">
                    {travel.reason}
                  </div>

                  {travel.status.startsWith('Pending') && travel.approvals_needed > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-xs text-muted-foreground">
                        <span className="flex items-center">
                          <span className="w-2 h-2 rounded-full mr-2 bg-yellow-600"></span>
                          {travel.approvals_needed} approval{travel.approvals_needed !== 1 ? 's' : ''} needed
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </TripsLayout>
  );
}
